import { useCallback, useEffect, useMemo, useState } from 'react';

import useCustomer from 'src/app/customers/hooks/useCustomer';
import featureFlagService from 'src/services/feature-flag-service';

import usePermissions from './usePermissions';

export type UseFeatureFlagOptions = {
    featureId: string;
    customerId?: string;
    ignoreInternalOverride?: boolean;
};

/**
 * Resolves a single feature flag via the CMS evaluate endpoint for **external** users.
 *
 * **Internal** users skip the network entirely: all features are treated as enabled
 * (`enabled === true`, `loading` resolves without calling the API).
 *
 * **External** users: `enabled` is exactly what CMS returns (`{ enabled }` from evaluate).
 *
 * Pass `ignoreInternalOverride` to always evaluate CMS and return its true value even
 * for internal users — use it to detect features an internal user can see but that are
 * not yet live for the customer.
 */
const useFeatureFlag = (options: UseFeatureFlagOptions) => {
    const { featureId, customerId: customerIdOption, ignoreInternalOverride = false } = options;

    const { currentCustomerId } = useCustomer();
    const { isCurrentUserInternal } = usePermissions();

    const effectiveCustomerId = useMemo(
        () => customerIdOption ?? (currentCustomerId ? currentCustomerId : undefined),
        [customerIdOption, currentCustomerId]
    );

    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (isCurrentUserInternal && !ignoreInternalOverride) {
            setEnabled(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await featureFlagService.evaluateFeatureFlag({
                featureId,
                customerId: effectiveCustomerId,
            });

            setEnabled(result.enabled);
        } finally {
            setLoading(false);
        }
    }, [featureId, effectiveCustomerId, isCurrentUserInternal, ignoreInternalOverride]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    return {
        enabled,
        loading,
        refresh,
    };
};

export default useFeatureFlag;
